import React, { useState, ChangeEvent } from 'react';
import { ArrowDownIcon, CalendarIcon, CloseIcon } from 'assets/icons';
import format from 'date-fns/format';
import styled from 'styled-components/macro';
import useClinicUser from 'shared/hooks/useClinicUser';
import sortBy from 'lodash-es/sortBy';
import {
  DemoWorkflowAppintmentWorkflowFragment,
  DemoWorkflowAppointmentFragment,
  SurveyStep,
  SurveyType,
  useGetDemoWorkflowClinicAppointmentsQuery,
  useTriggerDemoWorkflowEventMutation,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import useIsMobile from 'shared/hooks/useIsMobile';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Box } from '@televet/kibble-ui/build/chakra';

const WorkflowsDemo = (): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { isMobile } = useIsMobile();
  const [selectedAppointmentID, setSelectedAppointmentID] = useState('');
  const [selectedAppointmentAutomations, setSelectedAppointmentAutomations] = useState<
    DemoWorkflowAppintmentWorkflowFragment[]
  >([]);
  const [selectedAutomation, setSelectedAutomation] = useState<DemoWorkflowAppintmentWorkflowFragment>();
  const [triggerResponse, setTriggerResponse] = useState(false);
  const [isCalendarOnDisplay, setIsCalendarOnDisplay] = useState(false);
  const { isFeatureEnabled } = useFeatureFlag();

  /**
   * Initialize default date filter to be the past month ending in today
   * this code gets approximately one month before today,
   * correcting for when a previous month doesn't have the current date's number of days
   * (i.e. if today were March 31, Feb 31st does not exist)
   */
  const oneMonthAgo = new Date();
  const month = oneMonthAgo.getMonth();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  while (oneMonthAgo.getMonth() === month) {
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 1);
  }
  const initialStartDate = oneMonthAgo;
  initialStartDate.setHours(0, 0, 0, 0);
  const initialEndDate = new Date();
  initialEndDate.setHours(23, 59, 59, 999);
  const [selectionRange, setSelectionRange] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    key: 'selection',
  });

  const [triggerWorkflow] = useTriggerDemoWorkflowEventMutation();

  const { data: appointmentsData, loading: loadingAppointments } = useGetDemoWorkflowClinicAppointmentsQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
        startAt: { gte: selectionRange.startDate, lte: selectionRange.endDate },
      },
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    nextFetchPolicy: GraphQLFetchPolicies.NoCache,
  });

  return (
    <DemoWorkflowView>
      <ContentContainer>
        <WorkflowTriggerContainer>
          {loadingAppointments ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : null}
          {!loadingAppointments ? (
            <AppointmentWrapper>
              <StyledParagraph>Please select an appointment:</StyledParagraph>
              <FilterButtons>
                <FilterButton
                  onClick={(): void => {
                    setIsCalendarOnDisplay((isCalendarOnDisplay) => !isCalendarOnDisplay);
                  }}
                >
                  <StyledCalendarIcon /> Filter By Dates:{'  '}
                  {selectionRange.startDate !== selectionRange.endDate
                    ? `${format(new Date(selectionRange.startDate), "MMMM-dd-yy h:mm aaaaa'm' ")}-
                  ${format(new Date(selectionRange.endDate), " MMMM-dd-yy h:mm aaaaa'm'")}`
                    : format(new Date(selectionRange.startDate), "MMMM-dd-yy h:mm aaaaa'm' ")}
                  {isCalendarOnDisplay ? <StyledArrowUpIcon /> : <StyledArrowDownIcon />}
                </FilterButton>

                <FilterDropContainer>
                  <FilterDrop
                    onClick={(e): void => {
                      e.stopPropagation();
                    }}
                    display={`${isCalendarOnDisplay}`}
                  >
                    <StyledCloseIcon
                      onClick={(): void => setIsCalendarOnDisplay((isCalendarOnDisplay) => !isCalendarOnDisplay)}
                    />
                    <TimesSelector>
                      <TimeItem>
                        <Text as="p" size="sm">
                          Start at
                        </Text>
                        <Box maxW="90px" mx={2}>
                          <TimePicker
                            initialTime={selectionRange.startDate}
                            onTimeChange={(date): void => {
                              const newSelectionRange = { ...selectionRange };
                              newSelectionRange.startDate = date;
                              setSelectionRange(newSelectionRange);
                            }}
                            inputProps={{
                              rightElement: undefined,
                              size: 'sm',
                            }}
                            placement="bottom"
                          />
                        </Box>
                        <Text as="p" size="sm">
                          on:
                        </Text>
                      </TimeItem>
                      <TimeItem>
                        <Text as="p" size="sm">
                          End at
                        </Text>
                        <Box maxW="90px" mx={2}>
                          <TimePicker
                            initialTime={selectionRange.endDate}
                            onTimeChange={(date): void => {
                              const newSelectionRange = { ...selectionRange };
                              newSelectionRange.endDate = date;
                              setSelectionRange(newSelectionRange);
                            }}
                            inputProps={{
                              rightElement: undefined,
                              size: 'sm',
                            }}
                            placement="bottom"
                          />
                        </Box>
                        <Text as="p" size="sm">
                          on:
                        </Text>
                      </TimeItem>
                    </TimesSelector>
                    <DateRangePicker
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      months={isMobile ? 1 : 2}
                      rangeColors={['#39adc2']}
                      fixedHeight={true}
                      direction="horizontal"
                      ranges={[selectionRange]}
                      // eslint-disable-next-line
                      onChange={(e: any): void => {
                        e.selection.startDate = new Date(e.selection.startDate.setHours(0, 0, 0, 0));
                        e.selection.endDate = new Date(e.selection.endDate.setHours(23, 59, 59, 999));
                        setSelectionRange(e.selection);
                      }}
                    />
                  </FilterDrop>
                </FilterDropContainer>
              </FilterButtons>
              <StyledSelect
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  const appointmentId = e.target.value;
                  setTriggerResponse(false);
                  setSelectedAppointmentID(appointmentId);
                  const appointment = appointmentsData?.findManyAppointment?.find(
                    (appointment: DemoWorkflowAppointmentFragment) => {
                      return appointment.id === appointmentId;
                    },
                  );
                  const automations =
                    appointment?.workflows.filter(
                      (workflow) =>
                        !workflow.surveySettingId ||
                        (workflow.surveyStep === SurveyStep.Rating &&
                          ((isFeatureEnabled(FeatureFlagName.NpsSurvey) && workflow.surveyType === SurveyType.Nps) ||
                            workflow.surveyType === SurveyType.Csat)),
                    ) || [];
                  automations.push();
                  setSelectedAppointmentAutomations(automations);
                }}
              >
                <option value=""></option>
                {appointmentsData?.findManyAppointment
                  ?.filter((appointment) => {
                    return !!appointment.workflows?.length && !appointment.isDeleted;
                  })
                  ?.map((appointment, index) => {
                    return (
                      <option key={`pet-selection-${index}`} value={appointment.id}>
                        Pet: {appointment?.clinicPet?.name.trim()} Description:{' '}
                        {appointment.description.slice(0, 64).trim()}
                      </option>
                    );
                  })}
              </StyledSelect>
            </AppointmentWrapper>
          ) : null}

          {selectedAppointmentID ? (
            <WorkflowTypeWrapper>
              <StyledParagraph>Please Select Automation</StyledParagraph>
              <StyledSelect
                onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
                  const workflowEventSettingId = e.target.value;
                  setTriggerResponse(false);
                  setSelectedAutomation(
                    selectedAppointmentAutomations?.find((workflowEventSetting) => {
                      return workflowEventSetting.id === workflowEventSettingId;
                    }),
                  );
                }}
              >
                <option value=""></option>
                {sortBy(selectedAppointmentAutomations, (workflowEventSetting) => workflowEventSetting.createdAt).map(
                  (workflowEventSetting) => {
                    if (!workflowEventSetting.isPublished) {
                      return null;
                    }
                    return (
                      <option key={workflowEventSetting.id} value={workflowEventSetting.id}>
                        {workflowEventSetting.name}
                      </option>
                    );
                  },
                )}
              </StyledSelect>
            </WorkflowTypeWrapper>
          ) : null}

          {selectedAutomation?.id ? (
            <CallToActionWrapper>
              {triggerResponse ? <SuccessMessage>Workflow triggered successfully.</SuccessMessage> : null}
              <Button
                onClick={async (): Promise<void> => {
                  setTriggerResponse(false);

                  const response = await triggerWorkflow({
                    variables: {
                      data: {
                        entityId: selectedAppointmentID,
                        workflowId: selectedAutomation?.id,
                        event: selectedAutomation?.event,
                        delay: 1000,
                        actionNumber: 1,
                      },
                    },
                  });
                  setTriggerResponse(response?.data?.triggerWorkflowEvent?.success || false);
                }}
              >
                Trigger Workflow
              </Button>
            </CallToActionWrapper>
          ) : null}
        </WorkflowTriggerContainer>
      </ContentContainer>
    </DemoWorkflowView>
  );
};

export default WorkflowsDemo;

const LoadingContainer = styled.div`
  margin-top: 40px;
`;

const SuccessMessage = styled.p`
  margin-top: 10px;

  font-size: 20px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #5cc098;
`;

const CallToActionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const WorkflowTypeWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const AppointmentWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const StyledParagraph = styled.p`
  margin: 20px;

  font-size: 20px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
`;

const StyledSelect = styled.select`
  width: 80%;
  height: 40px;
  border-radius: 7px;
  border: solid 1px #d2d2d2;

  font-size: 16px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;

  margin: auto;
  padding-left: 10px;
`;

const DemoWorkflowView = styled.div`
  display: flex;
  height: 100%;
`;

const ContentContainer = styled.div`
  flex: 4;
  height: 100%;
`;

const WorkflowTriggerContainer = styled.div`
  align-items: center;
  justify-content: center;
  background-color: var(--chakra-colors-background-default);
  border-left: solid 1px #e8e9e9;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  scroll-behavior: smooth;

  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 0;
    width: 7px;
  }

  &::-webkit-scrollbar-track {
    margin: 5px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    box-shadow: rgba(255, 255, 255, 0.3) 0 0 0 1px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.35);
  }
`;
const TimesSelector = styled.div`
  padding-left: 225px;
  padding-top: 10px;
  display: flex;
  width: ${window.outerWidth <= 1575 ? '570px' : '889px'};
  padding-bottom: 10px;
  background-color: var(--chakra-colors-background-default);

  justify-content: flex-end;
`;

const TimeItem = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 498px;
  padding: 11px;
  padding-bottom: 0;
`;

const FilterButton = styled.div`
  display: flex;
  margin-left: 19px;
  padding: 10px;
  border-radius: 7px;
  border: solid 1px #d2d2d2;
  align-items: center;

  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.11px;

  cursor: pointer;
  user-select: none;
`;

interface IFilterDropProps {
  display: string;
}

const FilterDropContainer = styled.div``;
const FilterDrop = styled.div<IFilterDropProps>`
  display: ${({ display }): string => (display === 'true' ? 'block' : 'none')};
  position: absolute;
  top: 45px;
  right: ${window.outerWidth <= 1575 ? '0px' : '240px'};
  padding: 20px;
  border-radius: 7px;
  border: solid 1px #d2d2d2;
  z-index: 900;
  background-color: var(--chakra-colors-background-default);
`;

const StyledCalendarIcon = styled(CalendarIcon)`
  width: 11px;
  height: 11px;
  margin-right: 10px;
  svg {
    fill: #44496b;
    stroke: #44496b;
  }
`;

const StyledArrowUpIcon = styled(ArrowDownIcon)`
  transform: rotate(180deg);
  width: 11px;
  height: 11px;
  margin-left: 10px;
  svg {
    fill: #44496b;
    stroke: #44496b;
  }
`;

const StyledArrowDownIcon = styled(ArrowDownIcon)`
  width: 11px;
  height: 11px;
  margin-left: 10px;
  svg {
    fill: #44496b;
    stroke: #44496b;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  padding-bottom: 20px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  top: 20px;
  right: 20px;
  width: 20px;
  height: 20px;
  padding: 5px;
`;
